import isEqual from "lodash/isEqual";

import type { Position } from "@/shared/state/visualization/schema/v1/three-d";

/**
 * Determines how the effect should respond to the current render.
 *
 * - "external": entity.position was changed by something outside this component
 *   (e.g. the preset selector). The component should sync its local state to
 *   match entity.position and remount the DataSourceSelectors.
 *
 * - "internal": the component's own form fields changed. The component should
 *   build a new position from local state and dispatch it.
 *
 * - "none": nothing meaningful changed.
 */
export type ChangeOrigin = "external" | "internal" | "none";

/**
 * The current (broken) classification logic, extracted from PositionConfig's
 * useEffect. Uses only a ref comparison to distinguish external from internal
 * changes, with no awareness of in-flight dispatches.
 *
 * This is vulnerable to a race condition: if the component dispatches a
 * position update (setting positionRef ahead of entity.position) and an
 * unrelated re-render occurs before the reducer propagates, the mismatch
 * is incorrectly classified as "external," causing the DataSourceSelectors
 * to remount and wipe the user's path selection.
 */
export function classifyPositionChangeCurrent(
  lastKnownPosition: Position | undefined,
  entityPosition: Position | undefined,
  localPosition: Position | undefined,
): ChangeOrigin {
  if (!isEqual(lastKnownPosition, entityPosition)) {
    return "external";
  }

  if (!isEqual(localPosition, entityPosition)) {
    return "internal";
  }

  return "none";
}

/**
 * Fixed classification logic that accounts for in-flight dispatches.
 *
 * @param lastKnownPosition  The position the component last wrote to the
 *   reducer (via dispatch) or last observed from entity.position.
 * @param entityPosition  The position from the viz state reducer
 *   (entity.position) as of this render.
 * @param pendingDispatch  Whether the component has dispatched a position
 *   update that hasn't been reflected in entity.position yet. When true, a
 *   mismatch between lastKnownPosition and entityPosition is expected and
 *   should NOT be classified as external.
 * @param localPosition  The position built from the component's current local
 *   form state (forwardData, rightData, etc.).
 */
export function classifyPositionChange(
  lastKnownPosition: Position | undefined,
  entityPosition: Position | undefined,
  pendingDispatch: boolean,
  localPosition: Position | undefined,
): ChangeOrigin {
  const entityMatchesLastKnown = isEqual(lastKnownPosition, entityPosition);

  if (!entityMatchesLastKnown && !pendingDispatch) {
    return "external";
  }

  if (!entityMatchesLastKnown && pendingDispatch) {
    // Dispatch is in-flight; entity.position hasn't caught up yet.
    // Do nothing until it reflects our update.
    return "none";
  }

  if (!isEqual(localPosition, entityPosition)) {
    return "internal";
  }

  return "none";
}
